import React, { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import {
  BodyText,
  FadeView,
  Switch,
  TextField,
  theme,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import { CREDIT_CARDS_ICONS } from '../../../constants/credit-cards';
import type {
  ControlledFormProps,
  CreditCardField,
} from '../CreditCardForm.types';
import { BlockedFields } from './CreditCardForm.blocked';
import { Footer, LineItem } from './CreditCardForm.components';
import { LoadingFields } from './CreditCardForm.loading';

// Section of the credit card form controlled by us instead of Braintree.
export const CreditCardFormControlledContent = ({
  children,
  account,
  fadeDuration,
  isSaving,
  isDeleting,
  mode,
  isLoadingFields,
  canSubmitForm,
  form,
  excludedFields = [],
  onFormChange,
  onSubmitForm,
  onDelete,
}: ControlledFormProps) => {
  const { t } = useLocalizationContext();

  // ─────────────────────── Callbacks ──────────────────────────────────
  const updateFormField = useCallback(
    (field: CreditCardField) => (value: string | number | boolean) => {
      onFormChange({ ...form, [field]: value });
    },
    [form, onFormChange],
  );

  // ─────────────────────── Render ──────────────────────────────────
  return (
    <View testID="credit-card-form" style={styles.container}>
      <View style={styles.content}>
        {/* Loading placeholders for each field */}
        <FadeView duration={fadeDuration} show={isLoadingFields}>
          {mode !== 'edit' && <LoadingFields />}
        </FadeView>

        {/* Section Controlled by Braintree */}
        {children}

        {/* Used when editing credit card, fields disabled */}
        <BlockedFields
          show={Boolean(mode === 'edit')}
          account={account}
          cardType={CREDIT_CARDS_ICONS[account?.cardType ?? 'NEW']}
        />

        {/* Nickname field, not controlled by Braintree */}
        {!excludedFields.includes('nickname') && (
          <LineItem>
            <TextField
              testID="credit-card-form.nickname"
              label={t('credit-card-form.nickname')}
              value={form.nickname}
              onChangeText={updateFormField('nickname')}
            />
          </LineItem>
        )}

        {/* Default credit card switch, not controlled by Braintree */}
        {!excludedFields.includes('default') && (
          <LineItem style={styles.marginBottom}>
            <View style={styles.row}>
              <View style={styles.column}>
                <BodyText size={3}>{t('credit-card-form.default')}</BodyText>
              </View>

              <Switch
                testID="credit-card-form.default"
                value={form.default}
                accessibilityLabel={t('credit-card-form.default')}
                onChange={updateFormField('default')}
              />
            </View>
          </LineItem>
        )}
      </View>

      {/* Save / Remove footer, not controlled by Braintree */}
      <Footer
        mode={mode}
        isSaving={isSaving}
        isDeleting={isDeleting}
        canSubmit={canSubmitForm}
        onDelete={onDelete}
        onSubmit={onSubmitForm}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'space-between',
  },
  content: {
    flex: 1,
    paddingBottom: theme.spacing['6'],
  },

  column: {
    flex: 1,
  },
  row: {
    flexDirection: 'row',
  },
  marginBottom: {
    marginBottom: 0,
  },
});
